import { UNSLEEPING } from '@somnhold/content';
import type { BattleState, Spawn } from '../types.ts';
import { hexToWorld } from '../hex.ts';
import { ev, spawnEnemy } from './combat.ts';
import { facetakerSpawn } from './facetaker.ts';
import { unsleepingSpawn } from './unsleeping.ts';

/**
 * The ones with your face (El Párpado 3, docs/SAGAS.md): the titan dreams the group's own heroes, in black glass,
 * fighting on the nightmares' side. One comes out after another (the fighters' own first, then the absent ones'), each
 * with the kit of the Unsleeping of its class at a share of its strength and its player's face (as the Prestacaras
 * wears them, battle/facetaker.ts), going after whoever its player's spectre goes after. Nobody knows you like you do:
 * each hero hits their own copy twice as hard (battle/unsleeping.ts). Every copy felled wins it; the post falling, or
 * the outing running out, loses it, and the copies felled stay felled for the next one (campaign/sides.ts).
 */

export const copiesTick = (s: BattleState): void => {
  const r = s.rules?.copies;
  if (!r) return;
  const st = (s.copies ??= { next: 0, id: 0, at: r.first, fallen: [], done: 0 });
  if (!s.started || st.done) return;
  if (st.id) {
    const e = s.enemies.find((x) => x.id === st.id);
    if (e && e.hp > 0) return;
    const f = r.faces[st.next - 1];
    if (f) {
      st.fallen.push(f.pid);
      ev(s, { t: 'copy', kind: 'fell', pid: f.pid, x: e?.x ?? 0, y: e?.y ?? 0 });
    }
    st.id = 0;
    st.at = s.waveT + r.gap;
  }
  if (st.next >= r.faces.length) {
    st.done = s.tick;
    return;
  }
  if (s.waveT < st.at) return;
  const f = r.faces[st.next] as (typeof r.faces)[number];
  const hunt = r.hunt[st.next] ?? '';
  // Out of the eyelid's edge: the far end of the roads, one after another.
  const sec = s.map.sectors[st.next % s.map.sectors.length];
  const h = sec?.path[0] ?? { q: 0, r: 0 };
  const at = hexToWorld(h.q, h.r);
  const sp: Spawn = { tick: s.waveT, sector: st.next % (s.map.sectors.length || 1), kind: f.kind, tier: 3, elite: true, hp: r.hp, boss: true, copy: { faces: [f], hold: 0, rush: 0 }, un: { dmgBp: r.dmgBp, returns: UNSLEEPING.maxReturns, copy: true, of: f.pid, ...(hunt ? { hunt } : {}) } };
  const e = spawnEnemy(s, f.kind, 3, true, sp.sector, at.x, at.y, r.hp);
  s.bossId = e.id;
  unsleepingSpawn(s, e, sp);
  facetakerSpawn(s, e, sp);
  st.id = e.id;
  st.next++;
  ev(s, { t: 'copy', kind: 'out', pid: f.pid, x: e.x, y: e.y });
};

/** A blow from a hero on their own copy: twice as hard. */
export const copyOwnBp = (s: BattleState, of: string | undefined, pid: string): number => (of && of === pid ? (s.rules?.copies?.ownBp ?? 10000) : 10000);

/** Every copy felled: won (a moment later); the post fallen or the outing over: lost (what fell stays felled). */
export const copiesResult = (s: BattleState): 'won' | 'lost' | null => {
  const r = s.rules?.copies;
  if (!r) return null;
  if (s.keep.hp <= 0) return 'lost';
  if (s.copies?.done) return s.tick - s.copies.done >= r.beat ? 'won' : null;
  return s.started && s.waveT >= r.out ? 'lost' : null;
};
